import { useProfileImage, useProfileImageRemove } from 'all-in-one-shared-api';
import { Button, Card, message, Space } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import generateKey from 'utils/generateKey';
import { AvatarUpload } from './ProfileAvatarUpload';
import { StyledTitle } from 'components/StyledTitle';
import { ProfileAvatar as Avatar } from 'components/profileAvatar';
import { useKeycloakReady } from 'hooks';

const StyledProfileAvatarBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
`;

const StyledProfilePictureTitle = styled(Card)`
  width: 20%;
  background: var(--neutral-3);
  display: flex;
  align-items: center;
`;

const StyledProfilePictureBody = styled(Card)`
  width: 80%;
  background: white;
`;

const avatarStyles = {
  width: 45,
  height: 45,
};

export interface ProfileAvatarProps {
  memberType: string;
}

const ProfileAvatar = ({ memberType }: ProfileAvatarProps): JSX.Element => {
  const { t } = useTranslation();
  const { token, ready } = useKeycloakReady();
  const [removeKey, setRemoveKey] = useState('');

  const {
    shouldFetch: fetchProfileImage,
    data: profileImageData,
    mutate: mutateProfileImage,
  } = useProfileImage({ name: 'getProfileImage', memberType }, token ?? '', memberType, {
    revalidateOnFocus: false,
  });

  useProfileImageRemove(removeKey, token ?? '', {
    shouldRetryOnError: false,
    onSuccess: () => {
      message.success(t('profile.profilePicture.removeSuccess'));
      mutateProfileImage();
    },
    revalidateOnFocus: false,
  });

  useEffect(() => {
    fetchProfileImage(ready);
  }, [fetchProfileImage, ready]);

  const onRemoveProfileImage = () => {
    setRemoveKey(generateKey);
  };

  const profileImage =
    !!profileImageData && profileImageData.length > 0
      ? profileImageData.find((profileImage) => profileImage.memberType === memberType)
      : undefined;
  const avatarUrl = profileImage ? profileImage.pictureUrl : '';

  return (
    <>
      <StyledTitle level={4} margin="32px 0 10px 0">
        {t('profile.details')}
      </StyledTitle>
      <StyledProfileAvatarBody>
        <StyledProfilePictureTitle>{t('profile.avatar')}</StyledProfilePictureTitle>
        <StyledProfilePictureBody>
          <Space direction="vertical">
            <Avatar memberType={memberType} size="large" style={avatarStyles} />
            <Space direction="horizontal">
              <AvatarUpload
                child={<Button>{t('profile.changeAvatar')}</Button>}
                onUploadComplete={() => mutateProfileImage()}
              />
              <Button onClick={onRemoveProfileImage} disabled={!avatarUrl}>
                {t('profile.removeAvatar')}
              </Button>
            </Space>
          </Space>
        </StyledProfilePictureBody>
      </StyledProfileAvatarBody>
    </>
  );
};

export default ProfileAvatar;
